import React from 'react'
import NextHead from 'next/head'

import { buildBaseURL, buildCanonicalURL } from 'lib/utils/page'
import HrefLangs from 'src/comps/HrefLangs'
import { useMarket } from 'src/hooks/useMarket'
import { renderSrc } from 'src/helpers/imageHandler'
import { PropertyDetails } from '@s4l/stayforlong/property/domain/PropertyDetails'

interface Input {
  pathname: string
  locale: string
  propertyDetailsJSON?: ReturnType<PropertyDetails['toJSON']>
}

const Head = ({ pathname, locale, propertyDetailsJSON }: Input) => {
  const { market, language, subdomain, topdomain } = useMarket()

  if (!propertyDetailsJSON) {
    return null
  }

  const propertyDetails = PropertyDetails.fromJSONOrNull({ json: propertyDetailsJSON })

  if (!propertyDetails) return null

  const canonicalURL = buildCanonicalURL({ pathname, market, language, subdomain, topdomain })
  const baseURL = buildBaseURL({ subdomain, topdomain })

  const ldJSON = propertyDetails.getRichHeadJSON(canonicalURL)

  const mainImage = propertyDetails.propertyMainImage()
  const photoURL = mainImage
    ? renderSrc({
        imgObj: {
          bucket: mainImage.bucket,
          path: mainImage.path,
          width: mainImage.size.width,
          height: mainImage.size.height,
          code: mainImage.code,
        },
        width: 580,
      })
    : ''

  return (
    <>
      <NextHead>
        <title>{propertyDetails.propertyTitle()}</title>
        <meta name="title" content={propertyDetails.propertyTitle()} />
        <meta name="description" content={propertyDetails.propertyDescription()} />
        <meta property="og:title" content={propertyDetails.propertyTitle()} />
        <meta property="og:description" content={propertyDetails.propertyDescription()} />
        <meta property="og:url" content={canonicalURL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Stayforlong" />
        <meta property="og:image" content={photoURL} />
        <meta property="og:locale" content={locale.replace('-', '_')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:domain" content={baseURL} />
        <meta name="twitter:title" content={propertyDetails.propertyTitle()} />
        <meta name="twitter:description" content={propertyDetails.propertyDescription()} />
        <meta name="twitter:image" content={photoURL} />
        <meta name="twitter:url" content={canonicalURL} />
        <script
          id="hotel"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJSON) }}
        />
        <link rel="canonical" href={canonicalURL} />
      </NextHead>
      <HrefLangs pathname={pathname} />
    </>
  )
}

export default Head
